import { Injectable } from '@angular/core';
import * as Sentry from "@sentry/angular";
import { FirestoreService } from './firestore.service';
import { OnlineOfflineService } from './online-offine.service';
import { CorePosService } from './core-pos.service';
import { ModuleActivationService } from './module-activation.service';
import { CashTransactionSyncService } from './replication/cash-transaction-sync.service';
import { CashRegisterStatusLogSyncService } from './replication/cash-register-status-log-sync.service';
import { SalesOrderV3SyncService } from './replication/sales-order-v3-sync.service';
import { SalesOrderSyncService } from './replication/sales-order-sync.service';

@Injectable({
    providedIn: 'root'
})
export class PushDataService {

    private isSyncing = false; // Flag to indicate if sync is in progress

    constructor(
        private posService: CorePosService,
        private firestoreService: FirestoreService,
        private onlineOfflineService: OnlineOfflineService,
        private moduleActivationService: ModuleActivationService,
        private cashTransactionSyncService: CashTransactionSyncService,
        private cashRegisterStatusLogSyncService: CashRegisterStatusLogSyncService,
        private salesOrderV3SyncService: SalesOrderV3SyncService,
        private salesOrderSyncService: SalesOrderSyncService
    ) {

        if (`serviceWorker` in navigator) {
            navigator.serviceWorker.addEventListener('message', event => {
                if (event.data && event.data.command === 'syncSalesOrdersFinished') {
                    console.log('Sync Sales Orders finished.');
                }
            });
        }
    }

    pushLocalUpdatesToServer() {
        if (this.isSyncing) {
            console.log('Sync already in progress, skipping this request.');
            return;
        }

        this.isSyncing = true; // Set flag to indicate sync is in progress

        this.firestoreService.waitForPendingWrites().then(async () => {
            if ('serviceWorker' in navigator && 'SyncManager' in window) {
                navigator.serviceWorker.ready.then(swRegistration => swRegistration.sync.register('pushLocalUpdatesToServer'))
                    .catch(async () => {
                        // System was unable to register for a sync, this could be an OS-level restriction
                        await this.pushSalesOrdersToServer();
                        await this.cashTransactionSyncService.pushUnsyncedToServer();
                        await this.cashRegisterStatusLogSyncService.pushUnsyncedToServer();
                    });
            } else {
                // Serviceworker/sync not supported
                await this.pushSalesOrdersToServer();
                await this.cashTransactionSyncService.pushUnsyncedToServer();
                await this.cashRegisterStatusLogSyncService.pushUnsyncedToServer();
            }
        }).catch((error) => {
            console.error('Error waiting for pending writes.');
            Sentry.captureException(error);
        }).finally(() => {
            this.isSyncing = false; // Reset flag after sync completes
        });
    }

    private async pushSalesOrdersToServer() {
        const isPointOfSaleV3Enabled = await this.moduleActivationService.isPointOfSaleV3Enabled();
        if (isPointOfSaleV3Enabled) {
            await this.pushLocalCustomersToEdara3();
            await this.salesOrderV3SyncService.pushUnsyncedToServer();
        }
        else {
            // US 27474 Part 2: the replicated pipeline (UnSynced → Syncing →
            // Synced/Failed, sourced from Firestore only) replaces the legacy
            // Pending/Processing push.
            await this.salesOrderSyncService.pushUnsyncedToServer();
        }
    }

    private async pushLocalCustomersToEdara3() {
        try {
            if (this.onlineOfflineService.isOnline) {
                console.log('Pushing Local Customers V3 to the server ...\n');

                // Fetch pending Customers V3 from indexedDb
                console.log(`Fetching unsynced Customers V3 from IndexedDb ....\n`);
                let startDate = new Date();
                const pendingCustomers = await this.posService.getUnsyncedCustomersV3Async();
                let endDate = new Date();

                if (pendingCustomers.length > 0) {
                    console.log(`It took ${(endDate.getTime() - startDate.getTime())} ms.\n`);

                    console.log(pendingCustomers);

                    const chunkSize = 10;

                    for (let i = 0; i < pendingCustomers.length; i += chunkSize) {
                        const chunkOfCustomers = pendingCustomers.slice(i, i + chunkSize);

                        console.log('Syncing local Customers V3 to the server ...\n');
                        startDate = new Date();
                        let syncResults = await this.posService.syncCustomersV3ToServerAsync(chunkOfCustomers);

                        endDate = new Date();

                        if (syncResults) {
                            console.log(`Synced ${chunkOfCustomers.length} records to the server.\n`);
                            console.log(`It took ${(endDate.getTime() - startDate.getTime())} ms.\n`);

                            console.log(`Updating sync status of ${syncResults.length} Customer(s) V3 to IndexedDb ...\n`);
                            this.posService.saveSyncedCustomersV3ToLocalDb(syncResults);
                        }
                    }
                }
            }
        }
        catch (err) {
            Sentry.captureException(err);
            return Promise.reject('Sync local Customers V3 failed.');
        }
    }

}

